/* eslint-disable no-plusplus */
/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => {
  return {
    customTextField: {
      "& .MuiOutlinedInput-root": {
        minHeight: "36px",
        "& .MuiInputBase-input": {
          padding: "8.5px 14px"
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#dcdcdc"
        }
      }
    },
    adornmentWrapper: {
      display: "flex",
      alignItems: "center",
      border: "1px solid #dcdcdc",
      borderRadius: "4px 0 0 4px"
    },
    labelBox: {
      display: "flex",
      alignItems: "center",
      paddingRight: "8px"
    },
    verticalBar: {
      borderLeft: "1px solid #dcdcdc",
      height: "24px",
      marginLeft: "8px",
      marginRight: "8px"
    },
    chipContainer: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      flex: 1
    },
    chip: {
      margin: "0 4px 0 0"
    },
    extraChips: {
      marginLeft: "4px",
      cursor: "pointer"
    },
    subjectContainer: {
      fontSize: "14px"
    },
    subjectLabel: {
      opacity: "0.5",
      marginRight: "20px",
      minWidth: "60px"
    }
  };
});

const EmailInputField = ({ label, emails, setEmails, triggeredSendEmail }) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState("");
  const chipContainerRef = useRef(null);
  const [extraCount, setExtraCount] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const initialVisibleCount = 3; // Number of emails to show initially

  useEffect(() => {
    if (chipContainerRef.current) {
      const containerWidth = chipContainerRef.current.offsetWidth;
      let currentWidth = 0;
      let visibleCount = 0;

      for (let i = 0; i < emails.length; i++) {
        const chipWidth =
          (document.getElementById(`chip-${i}`)?.offsetWidth ?? 0) + 4; // 4px margin
        if (currentWidth + chipWidth > containerWidth) {
          setExtraCount(emails.length - visibleCount);
          return;
        }
        currentWidth += chipWidth;
        visibleCount++;
      }

      setExtraCount(0);
    }
  }, [emails]);

  const handleDelete = (emailToDelete) => {
    return () => {
      setEmails((prevEmails) => {
        return prevEmails.filter((email) => {
          return email !== emailToDelete;
        });
      });
    };
  };

  const handleAddEmail = (event) => {
    if (event.key === "Enter" && inputValue) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailPattern.test(inputValue)) {
        setEmails((prevEmails) => {
          return [...prevEmails, inputValue];
        });
        setInputValue("");
      }
      event.preventDefault();
    }
  };

  const handleToggleShowAll = () => {
    setShowAll((prevShowAll) => {
      return !prevShowAll;
    });
  };

  const displayedEmails = showAll
    ? emails
    : emails?.slice(0, initialVisibleCount);

  return (
    <TextField
      className={classes.customTextField}
      variant='outlined'
      fullWidth
      placeholder={`Enter ${label?.toLowerCase()} and press Enter`}
      value={inputValue}
      onChange={(e) => {
        return setInputValue(e.target.value);
      }}
      onKeyDown={handleAddEmail}
      disabled={label === "From"}
      error={label === "To" && triggeredSendEmail ? emails.length === 0 : false}
      helperText={
        label === "To" && triggeredSendEmail && emails.length === 0
          ? "Please enter an email to continue"
          : ""
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <Box className={classes.labelBox}>
              <span>{label}</span>
            </Box>
            <Box className={classes.verticalBar} />
            <Box className={classes.chipContainer} ref={chipContainerRef}>
              {label !== "From" ? (
                <>
                  {displayedEmails.map((email, index) => {
                    return (
                      <Chip
                        key={email}
                        id={`chip-${index}`}
                        label={email}
                        onDelete={handleDelete(email)}
                        className={classes.chip}
                      />
                    );
                  })}
                  {extraCount > 0 && !showAll && (
                    <Chip
                      label={`+${extraCount}`}
                      className={classes.extraChips}
                      onClick={handleToggleShowAll}
                    />
                  )}
                  {extraCount > 0 && showAll && (
                    <Chip
                      label='Hide'
                      className={classes.extraChips}
                      onClick={handleToggleShowAll}
                    />
                  )}
                </>
              ) : (
                <Chip
                  id={`chip-${0}`}
                  label={displayedEmails[0]}
                  // onDelete={handleDelete(email)}
                  className={classes.chip}
                />
              )}
            </Box>
          </InputAdornment>
        )
      }}
    />
  );
};

const EmailForm = (props) => {
  const [fromEmails, setFromEmails] = useState([]);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [toEmailString, setToEmailString] = useState([""]);
  const [ccEmailString, setCcEmailString] = useState([""]);
  const classes = useStyles();

  useEffect(() => {
    setFromEmails([
      ...fromEmails,
      props?.termSheetConfig?.email?.fromEmail ?? ""
    ]);
  }, [props?.termSheetConfig?.email]);

  useEffect(() => {
    // eslint-disable-next-line no-shadow
    const { toEmails = [], ccEmails = [] } = props?.termSheetData?.emails || {};

    // Update email arrays only if they are different from the current state
    setToEmails((prev) => {
      return JSON.stringify(prev) === JSON.stringify(toEmails)
        ? prev
        : [...toEmails];
    });
    setCcEmails((prev) => {
      return JSON.stringify(prev) === JSON.stringify(ccEmails)
        ? prev
        : [...ccEmails];
    });

    // Build email strings
    const tempToEmailString = toEmails.join(",") + (toEmails.length ? "," : "");
    const tempCcEmailString = ccEmails.join(",") + (ccEmails.length ? "," : "");

    setToEmailString(tempToEmailString);
    setCcEmailString(tempCcEmailString);
  }, [props?.termSheetData?.emails]);

  useEffect(() => {
    if (toEmails?.length || ccEmails?.length) {
      props?.termSheetConfig?.email?.handleEmailChange(
        toEmails,
        ccEmails,
        props?.termSheetData
      );
    }
  }, [toEmails, ccEmails]);

  return (
    <div>
      {!props?.termSheetConfig?.isViewMode &&
      (props?.termSheetConfig?.termSheetAction === "generate" ||
        props?.termSheetConfig?.termSheetAction === "edit") ? (
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid item xs={12}>
            <div style={{ display: "flex", padding: "4px" }}>
              <Typography className={classes.subjectLabel}>
                From: &nbsp;&nbsp;
              </Typography>
              <Typography>{fromEmails[0]}</Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <EmailInputField
              label='To'
              emails={toEmails}
              setEmails={setToEmails}
              triggeredSendEmail={props.triggeredSendEmail}
            />
          </Grid>
          <Grid item xs={12}>
            <EmailInputField
              label='CC'
              emails={ccEmails}
              setEmails={setCcEmails}
              triggeredSendEmail={props.triggeredSendEmail}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ padding: "22px" }}>
          {props?.termSheetConfig?.termSheetAction === "view-from-list" ? (
            <Grid item xs={12} className={classes.subjectContainer}>
              <div style={{ display: "flex" }}>
                <Typography className={classes.subjectLabel}>
                  From: &nbsp;&nbsp;
                </Typography>
                <Typography>{fromEmails[0]}</Typography>
              </div>
            </Grid>
          ) : null}

          <Grid item xs={12} className={classes.subjectContainer}>
            <div style={{ display: "flex" }}>
              <Typography className={classes.subjectLabel}>
                To: &nbsp;&nbsp;
              </Typography>
              <Typography>{toEmailString}</Typography>
            </div>
          </Grid>
          <Grid item xs={12} className={classes.subjectContainer}>
            <div style={{ display: "flex" }}>
              <Typography className={classes.subjectLabel}>
                CC: &nbsp;&nbsp;
              </Typography>
              <Typography>{ccEmailString}</Typography>
            </div>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default EmailForm;
